import {MOUSE} from "three";
import React, {useEffect, useState} from "react";
import {ipcRenderer, isStaticSite, storage} from "../electron-modules";
import {
  FormControl,
  List,
  ListItem,
  MenuItem,
  Select,
  Switch
} from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import { SingleSlider } from ".";
import { Viewer } from "../viewer/js/main";
import clsx from 'clsx';
import { buildElectronMenu } from "../electron-menu";
import { useTranslation } from "react-i18next";
import { SamplingRate, VolumeType } from "../viewer/js/measurements";
import { SimpleDialog } from "../components";

export enum ControlType {
  Encompass,
  Reverse,
  Autocad
}

export const ControlModes = {
  0: { ORBIT: MOUSE.LEFT, ZOOM: MOUSE.MIDDLE, PAN: MOUSE.RIGHT },
  1: { ORBIT: MOUSE.RIGHT, ZOOM: MOUSE.MIDDLE, PAN: MOUSE.LEFT },
  2: { ORBIT: -1, ZOOM: -1, PAN: MOUSE.MIDDLE }
};

export const initialState = {
  orthoQuality: 0.75,
  orthoOpacity: 1,
  controlType: ControlType.Encompass,
  language: "en",
  openQuickNav: true,
  volumeType: VolumeType.MultiPlane,
  volumeSampleRate: SamplingRate.Medium
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      marginTop: theme.spacing(1)
    },
    sectionTitle: {
      fontWeight: 'bold',
      display: "inline-block",
      fontSize: '0.9em'
    },
    slider: {
      width: "100%"
    },
    dropdown: {
      width: "40%"
    },
    checkBoxWithLabel: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    dropdownWithLabel: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    dialog: {
      "z-index": `${theme.zIndex.modal + 1} !important`
    },
    dialogTitleParent: {
      display: "flex",
      alignItems: "center"
    },
    dialogTitle: {
      userSelect: "none",
      flex: 1
    },
    close: {
      color: theme.palette.grey[500],
      marginRight: theme.spacing(1)
    }
  })
);

const loadGlobalSettings = () => {
  return new Promise(async resolve => {
    let globalSettings;

    if (isStaticSite) {
      try {
        const cache = Date.now();
        const response = await fetch(`./global-settings.json?cache=${cache}`);
        globalSettings = await response.json();
      } catch {
        console.error("Error reading global settings");
        globalSettings = {};
      }
    } else {
      const result = storage.get("global-settings");
      globalSettings = result.status ? result.data : {};
    }

    resolve({
      ...initialState,
      ...globalSettings
    });
  });
};

export const getGlobalSettings = async (key) => {
  const settings = await loadGlobalSettings();
  return settings[key];
};

export const GlobalSettings = (props) => {
  const viewer = props.viewer as Viewer;

  const {openQuickNav, setOpenQuickNav} = props;

  const classes = useStyles();
  const {t, i18n} = useTranslation();

  const [open, setOpen] = useState(false);
  const [orthoQuality, setOrthoQuality] = useState(null);
  const [orthoOpacity, setOrthoOpacity] = useState(null);
  const [controlType, setControlType] = useState(null);
  const [volumeType, setVolumeType] = useState(null);
  const [volumeSampleRate, setVolumeSampleRate] = useState(null);
  const [language, setLanguage] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);

  const handleSave = () => {
    storage.set("global-settings", {
      orthoQuality,
      orthoOpacity,
      controlType,
      language,
      openQuickNav,
      volumeType,
      volumeSampleRate
    });

    setOpen(false);
  };

  const getSettingsFromStorage = async () => {
    const settings = await loadGlobalSettings();
    setGlobalSettings(settings);
  };

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    getSettingsFromStorage();
  }, [viewer, open]);

  useEffect(() => {
    if (language === null) return;
    i18n.changeLanguage(language);
    buildElectronMenu();
    viewer?.updateCompass();
  }, [language]);

  useEffect(() => {
    if (controlType == null) return;
    viewer?.setControlType(controlType);
  }, [controlType]);

  useEffect(() => {
    if (orthoOpacity === null) return;
    viewer?.setOrthoOpacity(orthoOpacity);
  }, [orthoOpacity]);

  useEffect(() => {
    if (volumeType === null) return;
    viewer?.updateVolumeType(volumeType);
  }, [volumeType]);

  useEffect(() => {
    if (volumeSampleRate === null) return;
    viewer?.updateVolumeSampleRate(volumeSampleRate);
  }, [volumeSampleRate]);

  useEffect(() => {
    if (!globalSettings) return;
    setOrthoQuality(globalSettings.orthoQuality);
    setOrthoOpacity(globalSettings.orthoOpacity);
    setControlType(globalSettings.controlType);
    setLanguage(globalSettings.language);
    setOpenQuickNav(globalSettings.openQuickNav);
    setVolumeType(globalSettings.volumeType);
    setVolumeSampleRate(globalSettings.volumeSampleRate);
  }, [globalSettings]);

  useEffect(() => {
    ipcRenderer?.removeAllListeners("open-global-settings");
    ipcRenderer?.on("open-global-settings", () => {
      setOpen(true);
    });
  });

  return (
    <SimpleDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      canSubmit={true}
      title={t("global-settings.viewer-settings")}
      button={t('buttons.apply-settings')}
    >
      {/* Global Settings */}
      <List dense>

        {/* General setting header */}
        <ListItem>
          <Typography className={classes.sectionTitle} gutterBottom>
            {t('global-settings.general_settings')}
          </Typography>
        </ListItem>

        {/* Language */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t("global-settings.interface-language")}
          </Typography>

          <FormControl className={classes.dropdown}>
            <Select
              value={language}
              onChange={(event) => {
                const value = event.target.value as string;
                setLanguage(value);
              }}
            >
              <MenuItem key={0} value={"en"}>English</MenuItem>
              <MenuItem key={1} value={"de"}>Deutsch</MenuItem>
              <MenuItem key={2} value={"fr"}>Français</MenuItem>
              <MenuItem key={3} value={"es"}>Español</MenuItem>
              <MenuItem key={4} value={"ja"}>日本語</MenuItem>
            </Select>
          </FormControl>
        </ListItem>

        {/* Control Type */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t("global-settings.control-type")}
          </Typography>

          <FormControl className={classes.dropdown}>
            <Select
              value={controlType}
              onChange={(event) => {
                const value = Number(event.target.value);
                setControlType(value);
              }}
            >
              <MenuItem key={0} value={ControlType.Encompass}>
                {t("control-type.default")}
              </MenuItem>
              <MenuItem key={1} value={ControlType.Reverse}>
                {t("control-type.reverse")}
              </MenuItem>
              <MenuItem key={2} value={ControlType.Autocad}>
                {t("control-type.autocad")}
              </MenuItem>
            </Select>
          </FormControl>
        </ListItem>

        {/* Quick navigation */}
        <ListItem className={clsx(classes.header, classes.checkBoxWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t('global-settings.allow_quick_navigation_menu')}
          </Typography>

          <Switch
            checked={openQuickNav}
            onChange={() => setOpenQuickNav(!openQuickNav)}
            name="openQuickNav"
          />
        </ListItem>

        {/* Measurement header */}
        <ListItem className={classes.header}>
          <Typography className={classes.sectionTitle} gutterBottom>
            {t('global-settings.measurement_settings')}
          </Typography>
        </ListItem>

        {/* Volume Calculation */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t('global-settings.volume_calculation')}
          </Typography>

          <FormControl className={classes.dropdown}>
            <Select
              value={volumeType}
              onChange={(event) => {
                const value = Number(event.target.value);
                setVolumeType(value);
              }}
            >
              <MenuItem key={0} value={VolumeType.MultiPlane}>
                {t('measure.volume_multi_plane')}
              </MenuItem>
              <MenuItem key={1} value={VolumeType.LowestPlane}>
                {t('measure.volume_lowest_plane')}
              </MenuItem>
            </Select>
          </FormControl>
        </ListItem>

        {/* Volume Sampling */}
        <ListItem className={clsx(classes.header, classes.dropdownWithLabel)}>
          <Typography gutterBottom variant="subtitle2">
            {t('global-settings.volume_sample_rate')}
          </Typography>

          <FormControl className={classes.dropdown}>
            <Select
              value={volumeSampleRate}
              onChange={(event) => {
                const value = Number(event.target.value);
                setVolumeSampleRate(value);
              }}
            >
              <MenuItem key={0} value={SamplingRate.High}>
                {t('general.high')}
              </MenuItem>
              <MenuItem key={1} value={SamplingRate.Medium}>
                {t('general.medium')}
              </MenuItem>
              <MenuItem key={2} value={SamplingRate.Low}>
                {t('general.low')}
              </MenuItem>
            </Select>
          </FormControl>
        </ListItem>

        {/* Orthomoaic header */}
        <ListItem className={classes.header}>
          <Typography className={classes.sectionTitle} gutterBottom>
            {t("global-settings.orthomosaic-settings")}
          </Typography>
        </ListItem>

        {/* Orthomoaic opacity */}
        <ListItem>
          <div className={classes.slider}>
            <Typography gutterBottom variant="subtitle2">
              {t("global-settings.orthomosaic-opacity")}
            </Typography>
            <SingleSlider
              value={orthoOpacity}
              onChange={setOrthoOpacity}
              min={0}
              max={1}
              step={0.01}
              color={"secondary"}
              magnitude={100}
              endAdornment={"%"}
            />
          </div>
        </ListItem>

        {/* Orthomoaic quality */}
        <ListItem>
          <div className={classes.slider}>
            <Typography gutterBottom variant="subtitle2">
              {t("global-settings.orthomosaic-quality")}
            </Typography>
            <SingleSlider
              value={orthoQuality}
              onChange={setOrthoQuality}
              min={0.25}
              max={1.00}
              step={0.01}
              color={"secondary"}
              magnitude={100}
              endAdornment={"%"}
            />
          </div>
        </ListItem>

      </List>
    </SimpleDialog>
  );
};